import { createFileRoute, Link } from "@tanstack/react-router";
import { RouteErrorBoundary, RouteNotFoundBoundary } from "@/components/route-boundaries";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { AuctionGrid } from "@/components/auction-grid";
import { CardGridSkeleton, ErrorState } from "@/components/data-state";
import { Heart, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTranslation } from "@/i18n";

export const Route = createFileRoute("/_authenticated/watchlist")({
  head: () => ({ meta: [{ title: "Watchlist" }] }),
  component: WatchlistPage,
  errorComponent: RouteErrorBoundary,
  notFoundComponent: RouteNotFoundBoundary,
});

function WatchlistPage() {
  const { t } = useTranslation("activity");
  const { user } = useAuth();
  const { data, isLoading, isError, refetch, isRefetching } = useQuery({
    queryKey: ["watchlist", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("watchlist")
        .select("auctions(id,title,images,end_time,current_bid_pence,starting_price_pence,bid_count,grading_company,grade,seller_id,buy_now_enabled,buy_now_price_pence,keep_buy_now_after_bid,reservation_expires_at)")
        .eq("user_id", user!.id);
      if (error) throw error;
      return (data ?? []).map((r: any) => r.auctions).filter(Boolean);
    },
  });

  const count = data?.length ?? 0;

  return (
    <div className="relative">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-80 bg-gradient-to-b from-primary/[0.06] via-transparent to-transparent" />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 py-10 sm:py-14">
        <header className="mb-8 flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground/70">{t("watchlist.eyebrow", "Buyer")}</p>
            <h1 className="mt-2 font-display text-4xl sm:text-5xl flex items-center gap-3">
              {t("watchlist.title", "Watchlist")}
              {count > 0 && (
                <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-sm font-normal text-muted-foreground backdrop-blur-xl">
                  {count}
                </span>
              )}
            </h1>
            <p className="mt-2 text-sm text-muted-foreground">{t("watchlist.subtitle", "Cards you're tracking. Bid before they end.")}</p>
          </div>
          <Button asChild variant="outline" className="border-white/10 bg-white/5 backdrop-blur-xl hover:bg-white/10">
            <Link to="/auctions">{t("watchlist.browseAuctions", "Browse auctions")} <ArrowRight className="h-4 w-4 ml-2" /></Link>
          </Button>
        </header>

        {isLoading ? (
          <CardGridSkeleton count={8} />
        ) : isError ? (
          <ErrorState
            description={t("watchlist.errorDescription", "We couldn't load your watchlist.")}
            onRetry={() => refetch()}
            retrying={isRefetching}
          />
        ) : count === 0 ? (
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-xl p-16 text-center">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl border border-white/10 bg-white/5">
              <Heart className="h-6 w-6 text-primary" />
            </div>
            <h3 className="mt-5 font-display text-2xl">{t("watchlist.emptyTitle", "Your watchlist is empty")}</h3>
            <p className="mt-2 text-sm text-muted-foreground max-w-sm mx-auto">
              {t("watchlist.emptyDescription", "Browse live auctions and tap the heart to save the ones you love.")}
            </p>
            <Button asChild className="mt-6 bg-gold-gradient hover:opacity-90">
              <Link to="/auctions">{t("watchlist.discoverCards", "Discover cards")}</Link>
            </Button>
          </div>
        ) : (
          <AuctionGrid auctions={(data ?? []) as any} emptyText="" />
        )}
      </div>
    </div>
  );
}
